import { LitElement, html, css } from 'lit';
import { BootstrapCSS } from '../../resources/css/bootstrap.css';
import { EplibStyles } from '../../resources/css/style.css.js';

export class EplibLibBuildingView extends LitElement {

  static get styles() {
    return [
      BootstrapCSS,
      EplibStyles
    ];
  }

  async firstUpdated() {
    super.firstUpdated();
    document.title = 'EarlyPrint Library What\'s New';
    document.querySelector("meta[name='description']").setAttribute('content', 'What the EarlyPrint Library adds to its predecessors');
  }

  render() {
    return html`
    <div class="container-fluid">
      <div class="row justify-content-center">
          <div class="col-md-6">
            <div class="wrapper transparent">
              <div class="page-header">
                <h1>What is New About the EarlyPrint Library?</h1>
              </div>
              <div class="container-fluid">
                <div class="row">
                  <div>
                    <p>The EarlyPrint Library builds on the transcriptions of the Text Creation Partnership
                      (TCP), now released into the public domain. The TCP transcriptions are themselves
                      based on image collections available to subscribers to Early English Books
                      Online (EEBO) and EighteenthCentury Collections Online (ECCO), and the Evans-TCP
                      transcriptions of early American imprints. EEBO contains scans of about 130,000
                      items, about 55,000 of which were transcribed by the TCP. The selection emphasized
                      first editions and—barring special reasons—avoided duplication. &quot;Book&quot; and
                      &quot;duplicate&quot; are very slippery terms, but <i>grosso modo</i> it can be said that
                      the TCP archive comes close to having one copy of every ‘book’ from the first 230
                      years of English print in a digital and reasonably interoperable format. The ECCO
                      and Evans transcriptions are more selective but provide significant representation
                      of eighteenth-century British and early American (1640-1800) imprints, respectively.
                    </p>
                    <p>How does a &quot;complex digital surrogate for almost every book before 1700&quot;
                      differ from what already exists? First, it can provide new digital images that will be
                      free and of much better quality than the EEBO images, many of which are no longer up to current
                      standards. Second, there will be a way of fixing the many corrupt or missing words
                      that mar a quarter of the pages in the current transcriptions. Third, more powerful
                      metadata &quot;under the hood&quot; (notably word-level linguistic tagging) of the
                      visible text make the texts more computationally tractable, whether individually or
                      in the aggregate.</p>
                    <p>A few years ago at a Princeton conference on Research Data Life Cycle Management
                      (RDLCM), Brian Athey, the chair of Computational Medicine at the University of
                      Michigan, observed that &quot;agile data integration is an engine that drives
                      discovery.&quot; That is a very pithy phrase. Early modern books are certainly
                      &#39;research data&#39;. They have a &#39;life cycle&#39; that has entered a phase
                      in which the affordances of digital media create new challenges. For the
                      managemenet of such life cycles, Joshua Sosin, a papyrologist at Duke who played a
                      major role in the Integrating Digital Papyrology project, has argued forcefully for
                      &quot;investing greater data control in the user community.&quot;</p>
                    <p>Clay Shirky has written eloquently about &quot;cognitive surplus&quot; in his book
                      with that title. In a digital world a lot of people have lots of hours that they can
                      spend in different ways. Most of the 60,000 texts will benefit from some attention
                      of a housekeeping kind, and much of that attention can be given to services that
                      call on patience and attention to detail rather than highly specialized professional
                      competence and may be done in minutes or hours rather days and weeks at a time.</p>
                    <p>The Renaissance Society of America and the Shakespeare Association of America have
                      between them several thousand members, and their students are probably counted in
                      the tens of thousands. Together they have a lot of cognitive surplus. If a little of
                      it is spent every year on improving the EarlyPrint corpus, the cumulative effect of
                      five years’ work will be considerable.</p>
                    <p>The most distinctive feature of this site is the annotation capability that makes it
                      very easy for you to donate a little of your cognitive surplus and spend it on
                      textual work that improves the documentary infrastructure of Early Modern Studies.
                      The core of this feature is a data entry template for correcting the
                      most common forms of textual corruption. Anybody anytime and anywhere can contribute
                      to making the transcriptions more accurate or complete than they are now. Such
                      corrections are subject to editorial review. Once approved, they are incorporated
                      into the source file, with due credit given to the contributors. For more detail
                      consult the section on how to correct transcription errors.</p>
                    <p>Working with a text in this environment will be almost as easy as &quot;reading with
                      a pencil&quot;, but the results are more easily shareable. If you do textual work on
                      anything in the TCP corpus, we hope that you will find this site the easiest place
                      to do that work. That is a &quot;win-win&quot; scenario: what is easiest for you is
                      readily shareable with others. If a text you want to work on is part of the TCP
                      corpus or meets its structural conditions, it will be easy to add it. What can be
                      done with Early Modern texts, can also be done with other corpora, notably the
                      smaller TCP-Evans corpus of American texts before 1800. Step by step we may move
                      towards a &quot;Book of English&quot;, defined as</p>
                    <ul>
                      <li>A large, growing, collaboratively curated and public domain corpus</li>
                      <li>Of printed English from its earliest modern form</li>
                      <li>With full bibliographical detail</li>
                      <li>And light but consistent structural and linguistic annotation.</li>
                    </ul>
                    <h2 id="what-next-">What next?</h2>
                    <p>There are probably about 2,000 public domain image sets that can be mapped to TCP
                      texts, would be good enough for most scholarly purposes, and would in most cases be
                      significantly better than their corresponding EEBO images. Over the next few months
                      we will try to create digital combos of many of them. Unfortunately there is always
                      the need for a pair of human hands and eyes to adjust the alignment of text and
                      image, and we will look for routines to ease and share this burden. A growing set of
                      digital combos will add up to a motley collection, but we hope that it will persuade
                      users
                      of the attractiveness of old books in that new format. This is a good moment to draw
                      attention to the eloquent essay Together we can FrEEBO that John Overholt, the
                      curator of Early Modern books at Harvard&#39;s Houghton Library, wrote a few years
                      ago.</p>
                    <p>A group of undergraduate interns from Carleton, Grinnell, Knox, Lake Forest,
                      Northwestern, and Ohio Wesleyan experimented recently with TCP compatible
                      transcriptions of English Civil War pamphlets from</p>
                    <p>the Rare Book libraries of Brandeis and Queens University in Canada, and we have
                      incorporated their corrections into the site. It is clear that students learn a lot
                      from such an exercise and take some pleasure in creating something that others find
                      useful. </p>
                  </div>
                </div>
              </div>
            </div>
        </div>
      </div>
    </div>
  `;
  }
}

customElements.define('eplib-libbuilding-view', EplibLibBuildingView);